import React, { useRef, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Download, Upload, CheckCircle2, AlertTriangle, X } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * BulkImport — reusable UI for downloading an Excel template + uploading
 * a filled-in file. Handles server validation errors (row/column granularity).
 *
 * Props:
 *   entity: 'providers' | 'cost-centers' | 'launches'
 *   label: display name (e.g. "Prestadores")
 *   onDone: callback after successful import (parent refreshes list)
 */
const BulkImport = ({ entity, label, onDone, buttonClassName }) => {
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null); // { ok: bool, summary?, errors?, total_rows? }
  const inputRef = useRef(null);

  const templateUrl = `${API_URL}/bulk/templates/${entity}.xlsx`;
  const uploadUrl = `${API_URL}/bulk/${entity}`;

  const templateFileName = {
    'providers': 'template_prestadores.xlsx',
    'cost-centers': 'template_centros_de_custo.xlsx',
    'launches': 'template_lancamentos.xlsx',
  }[entity];

  const downloadTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(templateUrl, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = templateFileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Template baixado');
    } catch (e) {
      toast.error('Erro ao baixar template');
    }
  };

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    setResult(null);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(uploadUrl, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      setResult({ ok: true, ...res.data });
      toast.success('Importação concluída');
      onDone && onDone();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === 'object') {
        setResult({ ok: false, ...detail });
      } else {
        setResult({ ok: false, message: detail || 'Erro no upload', errors: [] });
      }
      toast.error('Falha na importação — veja detalhes');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <>
      <Button
        variant="outline"
        onClick={() => { setOpen(true); setResult(null); }}
        className={buttonClassName || 'rounded-sm'}
        data-testid={`bulk-import-open-${entity}`}
      >
        <Upload className="h-4 w-4 mr-1" /> Importar Excel
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-secondary uppercase tracking-tight">
              Importação em Massa — {label}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="p-3 bg-slate-50 border border-slate-200 rounded-sm text-sm text-slate-700">
              <p className="font-medium mb-1">Como usar:</p>
              <ol className="list-decimal list-inside space-y-1 text-xs">
                <li>Baixe o template abaixo (contém aba "Instruções" com regras de preenchimento).</li>
                <li>Preencha os dados no Excel (não altere os cabeçalhos).</li>
                <li>Salve como .xlsx e faça upload aqui.</li>
                <li>A importação é <strong>transacional</strong>: se qualquer linha tiver erro, <strong>nada</strong> é gravado.</li>
              </ol>
            </div>

            <div className="flex flex-col sm:flex-row gap-2">
              <Button variant="outline" onClick={downloadTemplate} className="flex-1 rounded-sm" data-testid={`bulk-download-${entity}`}>
                <Download className="h-4 w-4 mr-1" /> Baixar Template
              </Button>
              <label className="flex-1">
                <input
                  ref={inputRef}
                  type="file"
                  accept=".xlsx"
                  onChange={(e) => handleUpload(e.target.files?.[0])}
                  className="hidden"
                  data-testid={`bulk-upload-input-${entity}`}
                />
                <Button
                  type="button"
                  onClick={() => inputRef.current?.click()}
                  disabled={uploading}
                  className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-sm"
                  data-testid={`bulk-upload-btn-${entity}`}
                >
                  <Upload className="h-4 w-4 mr-1" /> {uploading ? 'Enviando…' : 'Selecionar Arquivo'}
                </Button>
              </label>
            </div>

            {/* Result panel */}
            {result?.ok && (
              <div className="p-4 border border-emerald-200 bg-emerald-50 rounded-sm" data-testid="bulk-result-success">
                <div className="flex items-center gap-2 text-emerald-700 font-medium">
                  <CheckCircle2 className="h-4 w-4" /> {result.message || 'Importação concluída'}
                </div>
                <div className="mt-2 text-sm text-slate-700">
                  <p>Linhas processadas: <strong>{result.processed ?? result.total_rows}</strong></p>
                  {result.summary && (
                    <p className="text-xs mt-1">
                      {result.summary.inserted != null && <>Inseridos: {result.summary.inserted}. </>}
                      {result.summary.updated != null && <>Atualizados: {result.summary.updated}.</>}
                    </p>
                  )}
                </div>
              </div>
            )}

            {result && !result.ok && (
              <div className="p-4 border border-red-200 bg-red-50 rounded-sm" data-testid="bulk-result-error">
                <div className="flex items-center gap-2 text-red-700 font-medium">
                  <AlertTriangle className="h-4 w-4" /> {result.message || 'Erros de validação'}
                </div>
                <p className="text-xs text-slate-600 mt-1">
                  {result.total_rows ? `${result.total_rows} linha(s) analisadas · ` : ''}
                  {result.errors?.length || 0} erro(s) encontrado(s). Nada foi gravado.
                </p>

                {result.errors?.length > 0 && (
                  <div className="mt-3 max-h-64 overflow-y-auto border border-slate-200 rounded-sm bg-white">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-100 sticky top-0">
                        <tr>
                          <th className="text-left px-2 py-1 font-medium">Linha</th>
                          <th className="text-left px-2 py-1 font-medium">Coluna</th>
                          <th className="text-left px-2 py-1 font-medium">Erro</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.errors.map((e, idx) => (
                          <tr key={idx} className="border-t border-slate-100">
                            <td className="px-2 py-1 text-slate-700 tabular-nums">{e.linha}</td>
                            <td className="px-2 py-1 text-slate-700 font-mono">{e.coluna}</td>
                            <td className="px-2 py-1 text-red-700">{e.erro}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end pt-2">
              <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">
                <X className="h-4 w-4 mr-1" /> Fechar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BulkImport;
